/**
 * Keep map-marker "Latest Event" caches in sync with websocket payloads.
 *
 * `useLatestEventForSensor` reads `queryKeys.events.latestBySensor(sensorId)`
 * (API sorted by startTimestamp desc). List/detail websocket updates do not
 * touch that key, so popups would stay stale until refetch.
 */

import type { Event, EventWithSensors } from "@sitescene/types";
import type { QueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/api/query-keys";
import {
	mergeWebsocketEventIntoCachedEvent,
	type MergeWebsocketEventOptions,
} from "@/utils/event-cache-merge";

const toTimeMs = (value: Date | null | number | string | undefined): number => {
	if (value instanceof Date) return value.getTime();
	if (typeof value === "string" || typeof value === "number") {
		const ms = new Date(value).getTime();
		return Number.isFinite(ms) ? ms : 0;
	}
	return 0;
};

/** Recency for latest-by-sensor: most recently started alert wins. */
export const latestEventRecencyMs = (event: Event): number =>
	toTimeMs(event.startTimestamp);

/** Sensor UUIDs from an event's sensors array (object or string refs). */
export const getSensorIdsFromEvent = (event: Event): string[] =>
	(event.sensors ?? [])
		.map((sensor) => (typeof sensor === "string" ? sensor : sensor.id))
		.filter((id): id is string => Boolean(id));

/**
 * True when `incoming` should replace (or update) the cached latest-by-sensor
 * entry. Same id always applies so title/status updates refresh an open popup.
 */
export const shouldReplaceLatestEventForSensor = (
	incoming: Event,
	cached: Event | null | undefined,
): boolean => {
	if (!cached) return true;
	if (cached.id === incoming.id) return true;
	return latestEventRecencyMs(incoming) >= latestEventRecencyMs(cached);
};

/**
 * Pick the better of an API fetch and an existing cache entry so an in-flight
 * popup fetch cannot clobber a newer websocket write that landed while it ran.
 */
export const pickPreferredLatestEvent = (
	fromApi: EventWithSensors | null,
	cached: EventWithSensors | null | undefined,
): EventWithSensors | null => {
	if (!fromApi) return cached ?? null;
	if (!cached) return fromApi;
	if (cached.id === fromApi.id) return fromApi;
	return shouldReplaceLatestEventForSensor(fromApi, cached) ? fromApi : cached;
};

/**
 * Write a websocket event into each related sensor's latest-event query cache
 * when it is as new or newer than what is already cached.
 */
export const updateLatestEventBySensorCaches = (
	queryClient: QueryClient,
	incoming: EventWithSensors,
	mergeOptions: MergeWebsocketEventOptions,
): void => {
	const sensorIds = getSensorIdsFromEvent(incoming);
	if (sensorIds.length === 0) return;

	for (const sensorId of sensorIds) {
		const queryKey = queryKeys.events.latestBySensor(sensorId);
		const cached = queryClient.getQueryData<EventWithSensors | null>(queryKey);

		if (!shouldReplaceLatestEventForSensor(incoming, cached)) {
			continue;
		}

		const next =
			cached && cached.id === incoming.id
				? mergeWebsocketEventIntoCachedEvent(cached, incoming, mergeOptions)
				: incoming;

		queryClient.setQueryData<EventWithSensors | null>(queryKey, next);
	}
};
